"use client";

import React from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./select";

export type PaginationControlsProps = {
	page: number;
	hasNextPage: boolean;
	hasPreviousPage: boolean;

	goNext: () => void;
	goPrevious: () => void;

	pageSize: number;
	onPageSizeChange: (n: number) => void;

	totalCount?: number;
	isFetching?: boolean;
	showPageCount?: boolean;
	className?: string;
	variant?: "default" | "page";
};

export function PaginationControls({
	page,
	hasNextPage,
	hasPreviousPage,
	goNext,
	goPrevious,
	pageSize,
	onPageSizeChange,
	totalCount,
	isFetching,
	showPageCount = true,
	className,
	variant = "default",
}: PaginationControlsProps) {
	const isPage = variant === "page";

	return (
		<div
			className={cn(
				"flex justify-between items-center",
				isPage && "py-6 px-4 md:px-0",
				className,
			)}
		>
			{showPageCount ? (
				<div className={cn("text-sm opacity-80", isPage && "hidden md:block")}>
					{totalCount !== undefined &&
						(totalCount > 0 ? (
							<>
								{page * pageSize + 1} to{" "}
								{Math.min((page + 1) * pageSize, totalCount)} of {totalCount}{" "}
								results
							</>
						) : (
							<>0 results</>
						))}
				</div>
			) : (
				<div />
			)}
			<div
				className={cn(
					"flex items-center gap-[10px] flex-shrink-0",
					isPage && "w-full justify-between md:w-auto md:justify-end",
				)}
			>
				<div className="flex gap-[10px] items-center">
					<span
						className={cn(
							"text-sm opacity-80 flex-shrink-0",
							isPage && "hidden md:block",
						)}
					>
						Results per page:
					</span>
					<Select
						value={pageSize.toString()}
						onValueChange={(value) => {
							onPageSizeChange(Number(value));
						}}
					>
						<SelectTrigger>
							<SelectValue placeholder="Select a page size" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="5">5</SelectItem>
							<SelectItem value="10">10</SelectItem>
							<SelectItem value="20">20</SelectItem>
							<SelectItem value="50">50</SelectItem>
							<SelectItem value="100">100</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<div className="flex gap-[10px] items-center">
					<Button
						onClick={goPrevious}
						disabled={!hasPreviousPage || isFetching}
						className="px-4"
						variant="outline"
						size="sm"
					>
						<ChevronLeftIcon className="size-4" />
						Previous
					</Button>
					<Button
						onClick={goNext}
						disabled={!hasNextPage || isFetching}
						className="px-4"
						variant="outline"
						size="sm"
					>
						Next
						<ChevronRightIcon className="size-4" />
					</Button>
				</div>
			</div>
		</div>
	);
}
